import { cx } from "../../lib/cx"
import { useInView } from "../../lib/useInView"

/**
 * What a long email chain looks like by reply forty, drawn and then animated.
 *
 * It plays once the reader reaches it: replies land one on top of the last,
 * the arrows between them draw themselves, and the questions nobody answered
 * appear at the end, after the noise has already settled. A still picture
 * makes the same point, but the pile-up is the point, and a pile-up is
 * something you watch happen.
 *
 * A real inbox would be somebody's private mail, so this is drawn. Anyone who
 * has asked for reduced motion gets the finished state immediately.
 */
export function EmailTangle({ className }) {
	const [ref, play] = useInView()

	return (
		<svg
			ref={ref}
			viewBox="0 0 520 420"
			className={className}
			role="img"
			aria-label="A tangle of overlapping email replies with unanswered questions running through them"
		>
			<defs>
				<linearGradient id="tangle-bg" x1="0" y1="0" x2="520" y2="420">
					<stop offset="0" stopColor="#fdf3e7" />
					<stop offset="1" stopColor="#fbe9e9" />
				</linearGradient>
				<marker
					id="tangle-arrow"
					viewBox="0 0 10 10"
					refX="8"
					refY="5"
					markerWidth="5"
					markerHeight="5"
					orient="auto"
				>
					<path d="M0 0 10 5 0 10z" fill="#c2410c" opacity="0.55" />
				</marker>
			</defs>

			<rect width="520" height="420" rx="20" fill="url(#tangle-bg)" />

			{/* Each reply lands on the one before it. */}
			{REPLIES.map((reply, index) => (
				<Envelope key={reply.subject} {...reply} play={play} delay={index * 260} />
			))}

			{/* Replies bouncing between people, crossing over each other. */}
			<g
				fill="none"
				stroke="#c2410c"
				strokeOpacity="0.45"
				strokeWidth="2.4"
				strokeLinecap="round"
			>
				{THREADS.map((d, index) => (
					<path
						key={d}
						d={d}
						pathLength="1"
						strokeDasharray="1"
						markerEnd="url(#tangle-arrow)"
						className={cx(play && "animate-draw")}
						style={{
							strokeDashoffset: play ? undefined : 1,
							animationDelay: `${420 + index * 260}ms`
						}}
					/>
				))}
			</g>

			{/* The questions that went past without an answer. */}
			{QUESTIONS.map(([x, y], index) => (
				<Unanswered
					key={`${x}-${y}`}
					x={x}
					y={y}
					play={play}
					delay={1500 + index * 180}
				/>
			))}
		</svg>
	)
}

const REPLIES = [
	{ x: 26, y: 30, w: 250, rot: -4, subject: "Cladding spec - east elevation", lines: 3 },
	{ x: 196, y: 72, w: 250, rot: 3, subject: "RE: Cladding spec - east ele…", lines: 2, flag: true },
	{ x: 48, y: 130, w: 262, rot: 2, subject: "RE: RE: Cladding spec", lines: 3 },
	{ x: 228, y: 182, w: 248, rot: -3, subject: "FW: RE: RE: Cladding spec", lines: 2, flag: true },
	{ x: 34, y: 236, w: 256, rot: -2, subject: "RE: FW: RE: RE: Cladding s…", lines: 3, flag: true },
	{ x: 214, y: 292, w: 254, rot: 4, subject: "RE: RE: FW: RE: RE: Claddi…", lines: 2 }
]

const THREADS = [
	"M150 74 C 300 40, 330 110, 250 122",
	"M300 130 C 210 150, 130 140, 120 176",
	"M170 196 C 300 206, 330 220, 320 236",
	"M330 248 C 220 266, 130 260, 128 286",
	"M180 300 C 280 320, 300 320, 300 338"
]

const QUESTIONS = [
	[432, 62],
	[468, 214],
	[402, 344],
	[24, 196]
]

/**
 * Two groups, not one.
 *
 * A CSS `transform` overrides the SVG `transform` attribute rather than
 * composing with it, so animating the group that carries the positioning
 * would land every envelope on the origin the moment the keyframes finish.
 * The outer group places it and the inner group does the moving.
 */
function Envelope({ x, y, w, rot, subject, lines, flag, play, delay }) {
	const h = 30 + lines * 11
	return (
		<g transform={`translate(${x} ${y}) rotate(${rot})`}>
			<g
				className={cx(play && "animate-drop-in")}
				style={{
					opacity: play ? undefined : 0,
					animationDelay: `${delay}ms`,
					transformBox: "fill-box",
					transformOrigin: "center"
				}}
			>
			<rect width={w} height={h} rx="7" fill="#ffffff" stroke="#e6d6c6" strokeWidth="1.4" />
			<circle cx="16" cy="17" r="7" fill={flag ? "#fde2c8" : "#e7edf1"} />
			<text
				x="30"
				y="20"
				fontFamily="system-ui, sans-serif"
				fontSize="10.5"
				fontWeight="600"
				fill="#3d4651"
			>
				{subject}
			</text>
			{Array.from({ length: lines }).map((_, index) => (
				<rect
					key={index}
					x="30"
					y={29 + index * 11}
					width={w - 30 - (index === lines - 1 ? 90 : 26)}
					height="4.5"
					rx="2.25"
					fill="#dbe2e8"
				/>
			))}
			{flag && <circle cx={w - 16} cy="17" r="4" fill="#bf5c0a" />}
			</g>
		</g>
	)
}

function Unanswered({ x, y, play, delay }) {
	return (
		<g transform={`translate(${x} ${y})`}>
			<g
				className={cx(play && "animate-pop")}
				style={{
					opacity: play ? undefined : 0,
					animationDelay: `${delay}ms`,
					transformBox: "fill-box",
					transformOrigin: "center"
				}}
			>
				<circle r="15" fill="#bf5c0a" fillOpacity="0.14" />
				<text
					textAnchor="middle"
					y="7"
					fontFamily="system-ui, sans-serif"
					fontSize="20"
					fontWeight="700"
					fill="#8f4407"
				>
					?
				</text>
			</g>
		</g>
	)
}
